import { useCallback } from "react";
import _ from "lodash";
import { toCurrency, currencyToStg } from "@/utils";
import { sortTimeSlots, removeAllSpaces } from "context/schedule/utils";
import {
    ScheduleFormInputs,
    Schedule,
    DeliverySchedule,
    PickupSchedule,
    DayName,
    DayInputs,
    StoreOption,
    Coordinates,
    ContextType,
} from "context/schedule/types";

export const useScheduleData = () => {
    // ============================================================================
    // DATA TRANSFORMATION HELPERS
    // ============================================================================

    const transformScheduleToInputs = useCallback(
        (schedule: Schedule, contextType: ContextType): ScheduleFormInputs => {
            const isDelivery = contextType === "Delivery";
            const deliverySchedule = schedule as DeliverySchedule;

            if (isDelivery) {
                const express = deliverySchedule.express_menu_config ?? {};
                const inventoriesIds =
                    express.inventory_provider_ids?.split(",") ?? [];

                return {
                    // Basic info
                    name: schedule.name ?? "",
                    note: schedule.note ?? "",
                    stores_provider_id: schedule.stores_provider_id,

                    // Working hours & time slots
                    working_hours: schedule?.working_hours ?? [],
                    time_slots: schedule.time_slots,

                    // Delivery specific
                    delivery_fee: currencyToStg(deliverySchedule.delivery_fee),
                    free_after: currencyToStg(deliverySchedule.free_after),
                    min: currencyToStg(deliverySchedule.min),
                    geo_type: deliverySchedule.geo_type ?? "ZIP",
                    zips: deliverySchedule.zips ?? "",
                    radius: deliverySchedule.radius ?? 1,
                    polygon_geo_zone: deliverySchedule.polygon_geo_zone ?? "",

                    // Express menu
                    active: !!express.active,
                    active_by_default: !!express.active_by_default,
                    start: express.start ?? "",
                    end: express.end ?? "",
                    expressName: express.name ?? "",
                    expressNote: express.note ?? "",
                    inventory_provider_ids: inventoriesIds,
                };
            }

            // Pickup schedule
            return {
                name: schedule.name ?? "",
                note: schedule.note ?? "",
                stores_provider_id: schedule.stores_provider_id,
                working_hours: schedule?.working_hours ?? [],
                time_slots: schedule.time_slots,
            };
        },
        [],
    );

    const transformInputsToSchedule = useCallback(
        (inputs: ScheduleFormInputs, contextType: ContextType): Schedule => {
            const base: PickupSchedule = {
                name: inputs.name,
                note: inputs.note,
                stores_provider_id: inputs.stores_provider_id,
                time_slots: sortTimeSlots(inputs.time_slots ?? []),
                working_hours: inputs.working_hours,
            };

            if (contextType === "Delivery") {
                const inv = Array.isArray(inputs.inventory_provider_ids)
                    ? inputs.inventory_provider_ids.join(",")
                    : inputs.inventory_provider_ids ?? "";

                return {
                    ...base,
                    free_after: toCurrency(inputs.free_after),
                    delivery_fee: toCurrency(inputs.delivery_fee),
                    min: toCurrency(inputs.min),
                    express_menu_config: {
                        inventory_provider_ids: removeAllSpaces(inv),
                        name: inputs.expressName,
                        note: inputs.expressNote,
                        active: !!inputs.active,
                        active_by_default: !!inputs.active_by_default,
                        start: inputs.start,
                        end: inputs.end,
                    },
                    geo_type: inputs.geo_type ?? "ZIP",
                    radius: inputs.radius,
                    polygon_geo_zone: inputs.polygon_geo_zone,
                    zips: removeAllSpaces(inputs.zips),
                } as DeliverySchedule;
            }

            return base;
        },
        [],
    );

    // ============================================================================
    // DAY SNAPSHOT HELPERS
    // ============================================================================

    const getDaySnapshot = useCallback(
        (
            day: DayName,
            inputs: ScheduleFormInputs,
            overrides?: Partial<DayInputs>,
        ): DayInputs => {
            const workingHoursArray = inputs?.working_hours ?? [];
            const wh =
                workingHoursArray.find(w => w.day_of_the_week === day) ?? null;

            const timeSlots =
                (inputs?.time_slots ?? []).filter(
                    ts => ts.day_of_the_week === day,
                ) ?? [];

            return {
                working_hours: overrides?.working_hours ?? wh,
                time_slots: overrides?.time_slots ?? timeSlots,
            };
        },
        [],
    );

    // ============================================================================
    // GEO HELPERS
    // ============================================================================

    const getAreaSelected = useCallback(
        (radius: number | undefined, storeCoordinates: Coordinates | null) => {
            let distance = 0;
            if (radius) distance = Number(radius) * 1609.34;

            return {
                center: {
                    lat: storeCoordinates?.lat ?? 0,
                    lng: storeCoordinates?.lng ?? 0,
                },
                radius: distance,
                type: "circle" as const,
            };
        },
        [],
    );

    // ============================================================================
    // INVENTORY HELPERS
    // ============================================================================

    const getInventorySelectedValues = useCallback(
        (
            inventoryIds: (string | number)[] | undefined,
            inventories: Array<{
                label: string;
                value: string | number;
                type?: string;
            }>,
        ) => {
            const ids = Array.isArray(inventoryIds) ? inventoryIds : [];
            return inventories?.filter(op => ids?.includes(op.value)) ?? [];
        },
        [],
    );

    // ============================================================================
    // EXPRESS MENU HELPERS
    // ============================================================================

    const handleExpressActiveToggle = useCallback(
        (
            checked: boolean,
            inputs: ScheduleFormInputs,
            setInputs: (inputs: ScheduleFormInputs) => void,
        ) => {
            if (!checked) {
                const updated = [...(inputs?.time_slots ?? [])].map(s => ({
                    ...s,
                    is_express: false,
                }));
                setInputs({ ...inputs, active: false, time_slots: updated });
                return;
            }
            setInputs({ ...inputs, active: checked });
        },
        [],
    );

    // ============================================================================
    // STORE PROVIDER HELPERS
    // ============================================================================

    const handleStoreProviderChange = useCallback(
        (
            option: StoreOption,
            inputs: ScheduleFormInputs,
            setInputs: (inputs: ScheduleFormInputs) => void,
            contextType: ContextType,
        ) => {
            if (inputs?.stores_provider_id === option?.value) return;

            if (contextType === "Delivery") {
                setInputs({
                    ...inputs,
                    stores_provider_id: option.value,
                    geo_type: "ZIP",
                    zips: "",
                    radius: 1,
                    polygon_geo_zone: "",
                    inventory_provider_ids: [],
                });
            } else {
                setInputs({
                    ...inputs,
                    stores_provider_id: option.value,
                });
            }
        },
        [],
    );

    // ============================================================================
    // INITIALIZATION HELPERS
    // ============================================================================

    const getInitialInputs = useCallback(
        (contextType: ContextType): Partial<ScheduleFormInputs> => {
            if (contextType === "Delivery") {
                return {
                    geo_type: "ZIP",
                    zips: "",
                    radius: 1,
                    polygon_geo_zone: "",
                    inventory_provider_ids: [],
                };
            }
            return {};
        },
        [],
    );

    // ============================================================================
    // VALIDATION HELPERS
    // ============================================================================

    const hasAnyWorkingHoursInWeek = useCallback(
        (workingHours: ScheduleFormInputs["working_hours"][0][]): boolean => {
            return workingHours?.some(w => !!w?.start && !!w?.end);
        },
        [],
    );

    return {
        // Transformations
        transformScheduleToInputs,
        transformInputsToSchedule,

        // Day helpers
        getDaySnapshot,

        // Geo helpers
        getAreaSelected,

        // Inventory helpers
        getInventorySelectedValues,

        // Express menu helpers
        handleExpressActiveToggle,

        // Store provider helpers
        handleStoreProviderChange,

        // Initialization
        getInitialInputs,

        // Validation helpers
        hasAnyWorkingHoursInWeek,
    };
};
